import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Case, Gender, ReminderStatus, Status } from "./types/Case";
import { DocumentResponseDto } from "./types/DocumentResponseDto";

export interface SingleCaseState {
  loading: boolean;
  error: string;
  currentCase: Case;
  consent: {
    loading: boolean;
    given: boolean;
    error: string;
  };
  documents: {
    data: DocumentResponseDto[];
    error?: string;
    loading: boolean;
  };
  cancellation: {
    loading: boolean;
    error?: string;
  };
}

const initialState: SingleCaseState = {
  loading: true,
  error: "",
  currentCase: {
    caseId: "",
    aggregatedStatus: undefined,
    finalizedLetterIdentifier: undefined,
    creationDateTime: "",
    customer: { personDto: { firstName: "", lastName: "", nin: "" } },
    evidences: [],
    expiresDateTime: "",
    gender: Gender.Unknown,
    insurances: [],
    insured: { firstName: "", lastName: "", nin: "" },
    reminderStatus: ReminderStatus.REMINDER_1,
    status: Status.INITIAL,
  },
  consent: {
    loading: false,
    given: false,
    error: "",
  },
  documents: {
    data: [],
    loading: false,
    error: undefined,
  },
  cancellation: {
    loading: false,
    error: "",
  },
};

interface SingleCaseStateRequest {
  caseId: string;
}

const singleCaseSlice = createSlice({
  name: "singlecase",
  initialState,
  reducers: {
    requestSingleCase: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<SingleCaseStateRequest>,
    ) => {
      state.loading = true;
      state.documents = initialState.documents;
    },
    requestSingleCaseSuccessful: (state, action: PayloadAction<Case>) => {
      state.loading = false;
      state.currentCase = action.payload;
      state.error = "";
    },
    requestSingleCaseFailed: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    requestDocumentsFromArchive: (
      state,
      action: PayloadAction<{ caseId: string }>,
    ) => {
      state.documents.loading = true;
    },
    requestDocumentsFromArchiveSuccessful: (
      state,
      action: PayloadAction<any>,
    ) => {
      state.documents.loading = false;
      state.documents.data = action.payload;
    },
    requestDocumentsFromArchiveFailed: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.documents.loading = false;
      state.documents.error = action.payload;
    },
    requestCancelCase: (state, action: PayloadAction<{ caseId: string }>) => {
      state.cancellation.loading = true;
      state.cancellation.error = "";
    },
    requestCancelCaseSuccessful: (state, action: PayloadAction<Case>) => {
      state.cancellation.loading = false;
      state.currentCase = action.payload;
    },
    requestCancelCaseFailed: (state, action: PayloadAction<string>) => {
      state.cancellation.loading = false;
      state.cancellation.error = "Kansellering av saken mislyktes. Prøv igjen.";
    },
  },
});

export const { actions } = singleCaseSlice;
export const {
  requestSingleCase,
  requestSingleCaseSuccessful,
  requestDocumentsFromArchive,
  requestCancelCase,
} = actions;

export default singleCaseSlice.reducer;
